import { useState, useCallback } from 'react'
import { comparePreview, compareFilesUpload, createFieldMapping, exportCompareWorkbook } from '../../../api'
import './CompareCard.css'

/**
 * CompareCard — file-compare flow rendered inline in the chat stream.
 *
 * Phases: upload → (preview) → mapping → running → done | error
 *
 * Props:
 *   compareMeta: { mode, truths, customer_id, source_hint, target_hint }
 *   onComplete: (fileCompareResponse, mapping) => void
 */
export default function CompareCard({ compareMeta = {}, onComplete }) {
  const customerId = compareMeta.customer_id || 'default'

  const [phase, setPhase] = useState('upload')
  const [sourceFile, setSourceFile] = useState(null)
  const [targetFile, setTargetFile] = useState(null)
  const [preview, setPreview] = useState(null)
  const [keyPair, setKeyPair] = useState(null)        // { source_field, target_field }
  const [pairs, setPairs] = useState([])              // [{ source_field, target_field, include }]
  const [remember, setRemember] = useState(true)
  const [useContext, setUseContext] = useState(true)
  const [result, setResult] = useState(null)
  const [error, setError] = useState('')
  const [srcSheet, setSrcSheet] = useState(null)
  const [tgtSheet, setTgtSheet] = useState(null)

  const runPreview = useCallback(async (sf, tf, sSheet, tSheet) => {
    setPhase('preview'); setError('')
    try {
      const p = await comparePreview({
        sourceFile: sf, targetFile: tf, customerId,
        sourceSheet: sSheet, targetSheet: tSheet,
      })
      if (!p.success) throw new Error(p.error || 'Could not profile the files.')
      setPreview(p)
      setSrcSheet(p.source_sheet || null)
      setTgtSheet(p.target_sheet || null)
      setKeyPair(p.suggested_key
        ? { source_field: p.suggested_key.source_field, target_field: p.suggested_key.target_field }
        : null)
      const compareSet = new Set(p.suggested_compare_fields || [])
      setPairs((p.field_mappings || []).map(m => ({
        source_field: m.source_field,
        target_field: m.target_field,
        include: compareSet.has(m.source_field),
      })))
      setPhase('mapping')
    } catch (e) {
      setError(e.message); setPhase('error')
    }
  }, [customerId])

  const onPickFile = (which, file) => {
    if (!file) return
    if (which === 'source') {
      setSourceFile(file)
      if (targetFile) runPreview(file, targetFile)
    } else {
      setTargetFile(file)
      if (sourceFile) runPreview(sourceFile, file)
    }
  }

  const runCompare = useCallback(async () => {
    if (!keyPair || !keyPair.source_field || !keyPair.target_field) {
      setError('Pick a key field on both the source and target side.'); return
    }
    setPhase('running'); setError('')
    try {
      // Only fully-specified field pairs count.
      const validPairs = pairs.filter(p => p.source_field && p.target_field)
      // Rename TARGET columns -> SOURCE names so one key/compare set matches both sides.
      const fieldMap = {}
      validPairs.forEach(p => { if (p.target_field !== p.source_field) fieldMap[p.target_field] = p.source_field })
      if (keyPair.target_field !== keyPair.source_field) fieldMap[keyPair.target_field] = keyPair.source_field

      const compareFields = validPairs.filter(p => p.include).map(p => p.source_field)
      const res = await compareFilesUpload({
        sourceFile, targetFile,
        keyField: keyPair.source_field,
        compareFields, fieldMap, customerId,
        sourceSheet: srcSheet, targetSheet: tgtSheet,
        useContext,
      })
      if (!res.success) throw new Error(res.error || 'Comparison failed.')
      setResult(res); setPhase('done')

      if (remember) {
        const allPairs = [
          keyPair,
          ...pairs.map(p => ({ source_field: p.source_field, target_field: p.target_field })),
        ]
        createFieldMapping({
          customerId,
          name: `filecompare_${(sourceFile.name || 'src').replace(/\W+/g, '_')}_${(targetFile.name || 'tgt').replace(/\W+/g, '_')}`.slice(0, 80),
          sourceSystem: compareMeta.source_hint,
          targetSystem: compareMeta.target_hint,
          pairs: allPairs,
        }).catch(e => setError(`The comparison ran, but its field mapping was not remembered: ${e.message}`))
      }

      if (onComplete) onComplete(res, { keyPair, pairs })
    } catch (e) {
      setError(e.message); setPhase('error')
    }
  }, [keyPair, pairs, sourceFile, targetFile, customerId, remember, useContext, compareMeta, onComplete, srcSheet, tgtSheet])

  const onSheetChange = (which, sheet) => {
    if (which === 'source') runPreview(sourceFile, targetFile, sheet, tgtSheet)
    else runPreview(sourceFile, targetFile, srcSheet, sheet)
  }

  const reset = () => {
    setPhase('upload'); setSourceFile(null); setTargetFile(null)
    setPreview(null); setKeyPair(null); setPairs([]); setResult(null); setError('')
    setSrcSheet(null); setTgtSheet(null)
  }

  const [exporting, setExporting] = useState(false)
  const downloadResult = async () => {
    const r = result || {}
    try {
      setExporting(true)
      const blob = await exportCompareWorkbook(r)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      const slug = (s) => String(s || 'file').replace(/[^A-Za-z0-9]+/g, '_').replace(/^_+|_+$/g, '')
      a.download = `reconciliation_${slug(r.source_file)}_vs_${slug(r.target_file)}.xlsx`.slice(0, 120)
      document.body.appendChild(a); a.click(); document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (e) {
      setError(e.message || 'Could not build the workbook.')
    } finally {
      setExporting(false)
    }
  }

  // ── UPLOAD ────────────────────────────────────────────────
  if (phase === 'upload' || phase === 'preview') {
    return (
      <div className="compare-card">
        <div className="compare-header">
          <div className="compare-icon">⇄</div>
          <div>
            <div className="compare-title">Compare two files</div>
            <div className="compare-subtitle">Excel or CSV · I'll suggest how to match them</div>
          </div>
        </div>
        <div className="compare-body">
          <div className="compare-drops">
            <FileDrop label="Source file" file={sourceFile} onPick={f => onPickFile('source', f)} />
            <div className="compare-vs">vs</div>
            <FileDrop label="Target file" file={targetFile} onPick={f => onPickFile('target', f)} />
          </div>
          {phase === 'preview' && (
            <div className="compare-status">Profiling columns and suggesting a mapping…</div>
          )}
        </div>
      </div>
    )
  }

  // ── MAPPING CONFIRM ───────────────────────────────────────
  if (phase === 'mapping') {
    const srcCols = (preview.source_columns || []).map(c => c.name)
    const tgtCols = (preview.target_columns || []).map(c => c.name)
    const noAutoMatch = (preview.field_mappings || []).length === 0
    const keyReady = !!(keyPair && keyPair.source_field && keyPair.target_field)
    return (
      <div className="compare-card">
        <div className="compare-header">
          <div className="compare-icon">⇄</div>
          <div>
            <div className="compare-title">Confirm the mapping</div>
            <div className="compare-subtitle">
              {preview.source_file} ({preview.source_rows.toLocaleString()} rows) ·
              {' '}{preview.target_file} ({preview.target_rows.toLocaleString()} rows)
            </div>
          </div>
        </div>
        <div className="compare-body">
          {((preview.source_sheets || []).length > 1 || (preview.target_sheets || []).length > 1) && (
            <div className="compare-sheets">
              {(preview.source_sheets || []).length > 1 && (
                <div className="compare-sheet-pick">
                  <div className="compare-field-label">Source sheet</div>
                  <select className="compare-key-select" value={srcSheet || ''}
                    onChange={e => onSheetChange('source', e.target.value)}>
                    {(preview.source_sheets || []).map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              )}
              {(preview.target_sheets || []).length > 1 && (
                <div className="compare-sheet-pick">
                  <div className="compare-field-label">Target sheet</div>
                  <select className="compare-key-select" value={tgtSheet || ''}
                    onChange={e => onSheetChange('target', e.target.value)}>
                    {(preview.target_sheets || []).map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              )}
            </div>
          )}

          {(hasContext(preview.source_context) || hasContext(preview.target_context)) && (
            <div className="compare-context-wrap">
              <div className="compare-context-head">
                Workbook context — these sheets aren't compared, but inform the analysis
              </div>
              <ContextPanel title={preview.source_file} ctx={preview.source_context} />
              <ContextPanel title={preview.target_file} ctx={preview.target_context} />
              {(hasPersistable(preview.source_context) || hasPersistable(preview.target_context)) && (
                <label className="compare-context-optin">
                  <input type="checkbox" checked={useContext} onChange={() => setUseContext(!useContext)} />
                  Remember this workbook's glossary &amp; reference tables for next time
                </label>
              )}
            </div>
          )}

          {noAutoMatch && (
            <div className="compare-hint">
              No columns auto-matched between these two sheets — likely different
              structures or languages. Pick the key field on each side, then add the
              fields you want to compare. If this looks like the wrong sheet, change
              the selection above.
            </div>
          )}

          <div className="compare-field-label">Match records on</div>
          <div className="compare-keyrow">
            <select className="compare-key-select" value={keyPair?.source_field || ''}
              onChange={e => setKeyPair(k => ({ source_field: e.target.value, target_field: (k && k.target_field) || '' }))}>
              <option value="" disabled>Source key…</option>
              {srcCols.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <span className="compare-pair-arrow">↔</span>
            <select className="compare-key-select" value={keyPair?.target_field || ''}
              onChange={e => setKeyPair(k => ({ source_field: (k && k.source_field) || '', target_field: e.target.value }))}>
              <option value="" disabled>Target key…</option>
              {tgtCols.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          <div className="compare-field-label" style={{ marginTop: 14 }}>Fields to compare</div>
          <div className="compare-pairs">
            {pairs.map((p, i) => (
              <div className="compare-pair-edit" key={i}>
                <input type="checkbox" checked={p.include}
                  onChange={() => setPairs(prev => prev.map((x, j) => j === i ? { ...x, include: !x.include } : x))} />
                <select className="compare-pair-select" value={p.source_field || ''}
                  onChange={e => setPairs(prev => prev.map((x, j) => j === i ? { ...x, source_field: e.target.value } : x))}>
                  <option value="" disabled>source column…</option>
                  {srcCols.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <span className="compare-pair-arrow">→</span>
                <select className="compare-pair-select" value={p.target_field || ''}
                  onChange={e => setPairs(prev => prev.map((x, j) => j === i ? { ...x, target_field: e.target.value } : x))}>
                  <option value="" disabled>target column…</option>
                  {tgtCols.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <button className="compare-pair-remove" title="Remove"
                  onClick={() => setPairs(prev => prev.filter((_, j) => j !== i))}>✕</button>
              </div>
            ))}
            <button className="compare-add-pair"
              onClick={() => setPairs(prev => [...prev, { source_field: '', target_field: '', include: true }])}>
              + Add field to compare
            </button>
          </div>

          <div className="compare-unmatched-note">
            {srcCols.length} source columns · {tgtCols.length} target columns available in the dropdowns above.
          </div>

          {error && <div className="compare-error-inline">{error}</div>}

          <div className="compare-footer">
            <label className="compare-remember">
              <input type="checkbox" checked={remember} onChange={() => setRemember(!remember)} />
              Remember this mapping for next time
            </label>
            <button className="compare-run-btn" onClick={runCompare} disabled={!keyReady}>
              Run comparison →
            </button>
          </div>
        </div>
      </div>
    )
  }

  // ── RUNNING ───────────────────────────────────────────────
  if (phase === 'running') {
    return (
      <div className="compare-card">
        <div className="compare-header">
          <div className="compare-icon">⇄</div>
          <div className="compare-title">Comparing…</div>
        </div>
        <div className="compare-body">
          <div className="compare-status">Matching records and analyzing variances…</div>
        </div>
      </div>
    )
  }

  // ── ERROR ─────────────────────────────────────────────────
  if (phase === 'error') {
    return (
      <div className="compare-card compare-card-error">
        <div className="compare-header">
          <div className="compare-icon compare-icon-error">!</div>
          <div className="compare-title">Comparison couldn't run</div>
        </div>
        <div className="compare-body">
          <div className="compare-error-inline">{error}</div>
          <button className="compare-run-btn" onClick={reset} style={{ marginTop: 12 }}>Start over</button>
        </div>
      </div>
    )
  }

  // ── DONE (consultative result) ────────────────────────────
  const r = result || {}
  const variances = r.variances || []
  return (
    <div className="compare-card compare-card-done">
      <div className="compare-header">
        <div className="compare-icon compare-icon-done">✓</div>
        <div>
          <div className="compare-title">Comparison complete</div>
          <div className="compare-subtitle">{r.source_file} vs {r.target_file} · key: {r.key_field}</div>
        </div>
      </div>
      <div className="compare-body">
        <div className="compare-stats">
          <Stat value={`${Math.round((r.match_rate || 0) * 100)}%`} label="Match rate" />
          <Stat value={(r.match_count || 0).toLocaleString()} label="Matched" />
          <Stat value={(r.variance_count || 0).toLocaleString()} label="Variances" tone="amber" />
          <Stat value={((r.missing_in_source || 0) + (r.missing_in_target || 0)).toLocaleString()} label="Missing" tone="red" />
        </div>

        {r.summary_narrative && (
          <div className="compare-narrative">{r.summary_narrative}</div>
        )}

        <FieldMapping mapping={r.field_mapping} />
        <RecordReconciliation recon={r.record_reconciliation} />

        <ContextApplied inventory={r.context_inventory} persisted={r.context_persisted} />

        {r.patterns && r.patterns.length > 0 && (
          <div className="compare-section">
            <div className="compare-section-title">Patterns</div>
            {r.patterns.map((p, i) => (
              <div className="compare-pattern" key={i}>
                <div className="compare-pattern-desc">{p.description}</div>
                {p.root_cause && <div className="compare-pattern-cause">Likely cause: {p.root_cause}</div>}
                {p.affected_count > 0 && <div className="compare-pattern-meta">Affects {p.affected_count.toLocaleString()} records</div>}
              </div>
            ))}
          </div>
        )}

        {r.recommendations && r.recommendations.length > 0 && (
          <div className="compare-section">
            <div className="compare-section-title">Recommendations</div>
            <ul className="compare-recs">
              {r.recommendations.map((rec, i) => <li key={i}>{rec}</li>)}
            </ul>
          </div>
        )}

        {variances.length > 0 && (
          <div className="compare-section">
            <div className="compare-section-title">
              Variances{r.variances_truncated ? ` (showing first ${variances.length} of ${(r.variance_count || 0).toLocaleString()})` : ''}
            </div>
            <div className="compare-var-table">
              {variances.slice(0, 15).map((v, i) => (
                <div className="compare-var-row" key={i}>
                  <div className="compare-var-key">{v.key}</div>
                  <div className="compare-var-fields">
                    {(v.fields || []).map((f, j) => (
                      <span className="compare-var-field" key={j}>
                        {f.field}: <b>{String(f.source)}</b>{f.source_label ? <span className="compare-var-label"> ({f.source_label})</span> : null} → <b>{String(f.target)}</b>{f.target_label ? <span className="compare-var-label"> ({f.target_label})</span> : null}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={{ marginTop: 14, display: 'flex', gap: 8, alignItems: 'center' }}>
          <button className="compare-run-btn" onClick={downloadResult} disabled={exporting}>
            {exporting ? 'Building workbook…' : '⬇ Download workbook'}
          </button>
          <button className="compare-run-btn compare-run-secondary" onClick={reset}>
            New comparison
          </button>
          {error && <span className="compare-error-inline" style={{ padding: 0 }}>{error}</span>}
        </div>
      </div>
    </div>
  )
}

function FileDrop({ label, file, onPick }) {
  return (
    <label className={`compare-drop ${file ? 'has-file' : ''}`}>
      <input
        type="file"
        accept=".csv,.xlsx,.xls"
        style={{ display: 'none' }}
        onChange={e => onPick(e.target.files && e.target.files[0])}
      />
      <div className="compare-drop-label">{label}</div>
      <div className="compare-drop-name">{file ? file.name : 'Click to choose…'}</div>
    </label>
  )
}

function Stat({ value, label, tone }) {
  return (
    <div className="compare-stat">
      <div className={`compare-stat-value ${tone ? `tone-${tone}` : ''}`}>{value}</div>
      <div className="compare-stat-label">{label}</div>
    </div>
  )
}

// ── Field mapping ─────────────────────────────────────────
function statusTone(s) {
  return s === 'clean' ? 'clean' : s === 'transform' ? 'transform' : 'discrepancy'
}
function fieldName(f) { return (f && typeof f === 'object') ? f.field : f }
function fieldCtx(f) { return (f && typeof f === 'object') ? (f.context || []) : [] }

function FieldMapping({ mapping }) {
  if (!mapping || !mapping.mappings) return null
  const jk = mapping.join_key
  const rows = mapping.mappings || []
  const tgtOnly = mapping.target_only_fields || []
  const c = mapping.counts || {}
  return (
    <div className="compare-section">
      <div className="compare-section-title">Field mapping</div>
      {jk && (
        <div className="compare-map-key">
          <span className="compare-map-keytag">key</span>
          <strong>{jk.source_column}</strong> ↔ <strong>{jk.target_column}</strong>
          <span className="compare-context-dim"> · {(jk.matched || 0).toLocaleString()} values overlap</span>
        </div>
      )}
      <div className="compare-map-table">
        {rows.map((row, i) => (
          <div className="compare-map-row" key={i}>
            <div className="compare-map-line">
              <div className="compare-map-cols">
                <span className="compare-map-src">{row.source}</span>
                <span className="compare-map-arrow">→</span>
                <span className="compare-map-tgt">{row.target || 'no counterpart'}</span>
              </div>
              <div className="compare-map-meta">
                {row.transform && <span className="compare-map-transform">{row.transform}</span>}
                <span className={`compare-status-badge ${statusTone(row.status)}`}>{row.status}</span>
              </div>
            </div>
            {(row.context || []).length > 0 && (
              <div className="compare-map-ctx">
                {row.context.map((x, j) => (
                  <span className="compare-map-ctx-item" key={j}>
                    <span className={`compare-context-tag ${x.role}`}>{x.role}</span>{x.annotation}
                  </span>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
      {tgtOnly.length > 0 && (
        <div className="compare-map-onlyrow">
          <span className="compare-map-onlylabel">In target only</span>
          {tgtOnly.map((f, i) => (
            <span className="compare-map-chip" key={i}
                  title={fieldCtx(f).map(x => x.annotation).join(' · ')}>
              {fieldName(f)}{fieldCtx(f).length > 0 ? ' •' : ''}
            </span>
          ))}
        </div>
      )}
      {mapping.validation_notes && (
        <div className="compare-map-valnotes">
          <div className="compare-map-valnotes-label">Validation notes</div>
          <div className="compare-map-valnotes-body">{mapping.validation_notes}</div>
        </div>
      )}
      <div className="compare-map-counts">
        {c.clean || 0} clean · {c.transform || 0} transform · {c.source_only || 0} in source only · {c.target_only || 0} in target only
      </div>
    </div>
  )
}

// ── Record reconciliation ─────────────────────────────────
function RecordReconciliation({ recon }) {
  if (!recon || !recon.counts) return null
  const c = recon.counts
  if ((c.source_only || 0) === 0 && (c.target_only || 0) === 0) return null
  const buckets = [
    ['source_only', 'In source only', recon.source_only || {}],
    ['target_only', 'In target only', recon.target_only || {}],
  ]
  return (
    <div className="compare-section">
      <div className="compare-section-title">Record reconciliation</div>
      <div className="compare-recon-counts">
        <span><strong>{(c.matched || 0).toLocaleString()}</strong> matched</span>
        <span><strong>{(c.source_only || 0).toLocaleString()}</strong> in source only</span>
        <span><strong>{(c.target_only || 0).toLocaleString()}</strong> in target only</span>
      </div>
      {recon.reasoning && <div className="compare-recon-reason">{recon.reasoning}</div>}
      {buckets.map(([k, label, b]) =>
        (c[k] || 0) > 0 && (b.discriminators || []).length > 0 ? (
          <div className="compare-recon-bucket" key={k}>
            <div className="compare-recon-bucket-label">{label} — what sets these apart</div>
            <ul className="compare-recon-evidence">
              {b.discriminators.map((d, i) => <li key={i}>{d.evidence}</li>)}
            </ul>
          </div>
        ) : null
      )}
    </div>
  )
}

// Workbook context helpers — what was recognized on a side beyond the data sheet.
function hasContext(ctx) {
  return !!ctx && Object.keys(ctx).length > 0
}
function hasPersistable(ctx) {
  return !!ctx && (!!ctx.glossary || (ctx.lookups || []).length > 0)
}

function ContextApplied({ inventory, persisted }) {
  const items = (inventory && inventory.items) || []
  const records = items.filter(i => i.link_kind === 'record')
  const columns = items.filter(i => i.link_kind === 'column')
  const savedCount = ((persisted && persisted.created) || []).length
  if (records.length === 0 && columns.length === 0 && savedCount === 0) return null

  const resolved = columns.map(i => i.data_column).filter(Boolean)
  return (
    <div className="compare-section">
      <div className="compare-section-title">Context applied</div>
      <ul className="compare-context-applied">
        {records.map((i, k) => (
          <li key={`rec-${k}`}>
            <span className="compare-context-tag errors">{i.role}</span>
            <strong>{i.sheet}</strong> linked by <code>{i.link_column}</code> — {(i.records_linked || 0).toLocaleString()} records
          </li>
        ))}
        {columns.length > 0 && (
          <li>
            <span className="compare-context-tag lookup">reference</span>
            {columns.length} table{columns.length === 1 ? '' : 's'} resolving{' '}
            <span className="compare-context-dim">{resolved.slice(0, 6).join(', ')}{resolved.length > 6 ? `, +${resolved.length - 6} more` : ''}</span>
          </li>
        )}
      </ul>
      {savedCount > 0 && (
        <div className="compare-context-saved">
          ✓ Saved {savedCount} item{savedCount === 1 ? '' : 's'} for reuse
          {persisted.glossary_terms > 0 ? ` — glossary (${persisted.glossary_terms} terms)` : ''}
        </div>
      )}
      {inventory && (inventory.resolved_columns || []).length > 0 && (
        <div className="compare-context-resolved">
          Codes resolved to labels in: {inventory.resolved_columns.join(', ')}
        </div>
      )}
    </div>
  )
}

function ContextPanel({ title, ctx }) {
  if (!hasContext(ctx)) return null
  const g = ctx.glossary
  const lookups = ctx.lookups || []
  const rules = ctx.rules_sheets || []
  const errors = ctx.errors_sheets || []
  const lookupNames = lookups.map(l => l.sheet)
  return (
    <div className="compare-context">
      <div className="compare-context-file">{title}</div>
      <ul className="compare-context-items">
        {g && (
          <li>
            <span className="compare-context-tag glossary">Glossary</span>
            {g.terms} terms <span className="compare-context-dim">({g.source_col} → {g.target_col})</span>
          </li>
        )}
        {lookups.length > 0 && (
          <li>
            <span className="compare-context-tag lookup">Reference</span>
            {lookups.length} table{lookups.length === 1 ? '' : 's'}
            <span className="compare-context-dim"> — {lookupNames.slice(0, 6).join(', ')}{lookupNames.length > 6 ? `, +${lookupNames.length - 6} more` : ''}</span>
          </li>
        )}
        {rules.length > 0 && (
          <li><span className="compare-context-tag rules">Rules</span>{rules.join(', ')}</li>
        )}
        {errors.length > 0 && (
          <li><span className="compare-context-tag errors">Errors</span>{errors.join(', ')}</li>
        )}
      </ul>
    </div>
  )
}
